import {
	captureErrorToSentry,
	captureWarningToSentry,
} from "@helpers/sentry-logger";
import {
	type PollSessionAssessmentStatusOptions,
	pollSessionAssessmentStatus,
} from "./poll-session-assessment-status";

export type RunSummativeSubmitGateParams = {
	sessionId: number;
	aiItemIds: number[];
	endSession: () => unknown;
	onProgress?: (completed: number, total: number) => void;
	intervalMs?: number;
	timeoutMs?: number;
	sleep?: PollSessionAssessmentStatusOptions["sleep"];
};

// endSession runs even on poll timeout; only a Sentry log is needed, not blocking submit, as the server replays pending items.
export const runSummativeSubmitGate = async (
	params: RunSummativeSubmitGateParams,
): Promise<void> => {
	const {
		sessionId,
		aiItemIds,
		endSession,
		onProgress,
		intervalMs,
		timeoutMs,
		sleep,
	} = params;

	const result = await pollSessionAssessmentStatus(sessionId, aiItemIds, {
		intervalMs,
		timeoutMs,
		onProgress,
		sleep,
	});

	if (!result.allComplete) {
		// A single pending item is normal — it's the one enqueued at submit,
		// and its grading often takes longer than the poll budget. More than
		// one pending means grading is actually stuck.
		const isSingleItemPending = result.completed === result.total - 1;
		if (isSingleItemPending) {
			captureWarningToSentry(
				"Summative submit gate: assessment polling timed out before submit",
				{ sessionId, completed: result.completed, total: result.total },
			);
		} else {
			captureErrorToSentry(
				new Error(
					"Summative submit gate: assessment polling timed out before submit",
				),
				{ sessionId, completed: result.completed, total: result.total },
			);
		}
	}

	endSession();
};
